(function () {
    'use strict';

    angular
        .module('app')
        .directive('drvEntityList', drvEntityList);
    
    /**
     * @ngdoc directive
     * @name drvEntityList
     *
     * @description
     * Present a list of entities, including option to navigate to entity and perform bulk operations on selection.
     * 
     * @param {string} label - Text to be shown for header label
     * @param {object} configuration
     * @param viewMode
     */
    function drvEntityList() {
        return {
            templateUrl: 'app/dashboard/entity-list.tmpl.html',
            scope: {},
            controller: drvEntityListCtrl,
            controllerAs: 'drvCtrl',
            bindToController: {
                label: '=?',
                configuration: '=',
                viewMode: '='
            }
        };
    }

    drvEntityListCtrl.$inject = [
        '$scope',
        '$q',
        '$location',
        'ChilliUserProfileService',
        'EntitiesService',
        'EntityTemplatesService',
        'FileSelectorService',
        'DocMergeService',
        'DialogService',
        'NavigationAidService',
        'SystemService',
        'WorkflowRunService',
        'PromiseLatest'];

    function drvEntityListCtrl(
        $scope,
        $q,
        $location,
        ChilliUserProfileService,
        EntitiesService,
        EntityTemplatesService,
        FileSelectorService,
        DocMergeService,
        DialogService,
        NavigationAidService,
        SystemService,
        WorkflowRunService,
        PromiseLatest) {

        /* jshint validthis:true */
        var drvCtrl = this;
        drvCtrl.allEntitiesSelected = false;
        drvCtrl.label = '';
        drvCtrl.entities = [];
        drvCtrl.entityCount = 0;
        drvCtrl.currentPage = 1;
        drvCtrl.deleteEntity = deleteEntity;
        drvCtrl.updateList = updateList;
        drvCtrl.mergeDocument = mergeDocument;
        drvCtrl.addNote = addNote;
        drvCtrl.addFile = addFile;
        drvCtrl.composeEmails = composeEmails;
        drvCtrl.composeSMS = composeSMS;
        drvCtrl.addTask = addTask;
        drvCtrl.deleteEntities = deleteEntities;
        drvCtrl.copyEntities = copyEntities;
        drvCtrl.exportEntities = exportEntities;
        drvCtrl.importEntities = importEntities;
        drvCtrl.newEntityLink = newEntityLink;
        drvCtrl.runWorkflow = runWorkflow;
        drvCtrl.selectedOrderFieldChange = selectedOrderFieldChange;
        drvCtrl.orderSelectorDisabled = orderSelectorDisabled;
        drvCtrl.newEntity = newEntity;
        drvCtrl.close = close;
        drvCtrl.fullscreen = fullscreen;
        drvCtrl.loadEntityCount = loadEntityCount;

        drvCtrl.loading = false;
        drvCtrl.error = null;
        drvCtrl.showDeleted = false;
        drvCtrl.orderFields = [];
        Object.defineProperty(drvCtrl, 'selectedEntities', {
            get: function () {
                return _(drvCtrl.entities)
                    .filter('Selected')
                    .value();
            }
        });
        drvCtrl.searchFields = {};
        drvCtrl.searchText = '';
        Object.defineProperty(drvCtrl, 'searchLabel', { get: getSearchLabel });
        Object.defineProperty(drvCtrl, 'userIsAdministrator', {
            get: function () { return ChilliUserProfileService.userIsAdministrator; }
        });
        Object.defineProperty(drvCtrl, 'titleText', { get: getTitleText });

        var updateListPromise = new PromiseLatest();

        activate();

        function activate() {
            $scope.$watch('drvCtrl.showDeleted', showDeletedChanged);

            drvCtrl.searchDelay = SystemService.getSearchDelay();

            loadEntityTemplate().then(function () {
                if (!drvCtrl.error) {
                    return loadEntityCount()
                        .finally(function () {
                            if (!drvCtrl.error) {
                                updateList();
                            }
                        });
                }
            });
        }

        /**
         * Load the entity template for the entities being displayed in the list.
         */
        function loadEntityTemplate() {
            return EntityTemplatesService.get(drvCtrl.configuration.EntityTemplateId)
                .then(getEntityTemplateOk, getEntityTemplateError);

            function getEntityTemplateOk(data) {
                drvCtrl.entityTemplate = data;

                if (_.isNil(drvCtrl.entityTemplate)) {

                    // Error loading entity template
                    drvCtrl.error = 'Unable to load entity template with Id - ' + drvCtrl.configuration.EntityTemplateId;

                } else if (!drvCtrl.entityTemplate.Standalone) {

                    // Entity template is not for standalone entities and isn't a valid route
                    drvCtrl.error = 'Entity template ' + drvCtrl.entityTemplate.Name + ' is for non-standalone ' + drvCtrl.entityTemplate.PluralName;

                } else {

                    // Successfully loaded entity template, configure view accordingly

                    drvCtrl.label = drvCtrl.entityTemplate.PluralName;

                    // Watch for update on search text, update list if it changes
                    $scope.$watch('drvCtrl.searchText', function (newVal, oldVal) {
                        if (newVal !== oldVal) {
                            updateList(1);
                        }
                    });

                    // Setup "order by" settings
                    if (drvCtrl.entityTemplate.OrderSetting.Options.length > 0) {
                        if (drvCtrl.entityTemplate.OrderSetting.Default !== '') {
                            drvCtrl.selectedOrderOption = drvCtrl.entityTemplate.OrderSetting.Default;
                        } else {
                            drvCtrl.selectedOrderOption = drvCtrl.entityTemplate.OrderSetting.Options[0].Name;
                        }

                        drvCtrl.orderOptions = _.map(drvCtrl.entityTemplate.OrderSetting.Options, function (orderOption) {
                            return { value: orderOption.Name, label: orderOption.Name };
                        });

                        updateOrderFields();
                    }
                }
            }

            function getEntityTemplateError(response) {
                drvCtrl.error = 'Error fetching entity template with Id - ' + drvCtrl.configuration.EntityTemplateId;
            }
        }

        function loadEntityCount() {
            return EntityTemplatesService.getEntityCount(drvCtrl.configuration.EntityTemplateId, drvCtrl.showDeleted)
                .then(loadEntityCountOk, loadEntityCountError);

            function loadEntityCountOk(entityCount) {
                drvCtrl.totalEntityCount = entityCount;
            }

            function loadEntityCountError(response) {
                drvCtrl.error = 'Error fetching entity count for ' + drvCtrl.entityTemplate.Name;
            }
        }

        /**
         * Get the label, based on entity template, to use for the "Search" field.
         *
         * @returns {string}
         */
        function getSearchLabel() {
            var searchLabel = '';

            if (!_.isNil(drvCtrl.entityTemplate) && !_.isNil(drvCtrl.entityTemplate.SearchFields) && drvCtrl.entityTemplate.SearchFields.length > 0) {
                if (drvCtrl.entityTemplate.SearchFields.length > 1) {
                    searchLabel = ' or ' + drvCtrl.entityTemplate.SearchFields[drvCtrl.entityTemplate.SearchFields.length - 1];
                }

                for (var i = drvCtrl.entityTemplate.SearchFields.length - 2; i > 0; i--) {
                    searchLabel = ', ' + drvCtrl.entityTemplate.SearchFields[i] + searchLabel;
                }

                searchLabel = 'Search by ' + drvCtrl.entityTemplate.SearchFields[0] + searchLabel;
            }

            return searchLabel;
        }

        /**
         * Refresh the list of entities.
         *
         * @param {number} newPage - Optional. Override the page to be refreshed, useful for going back to page 1.
         */
        function updateList(newPage) {
            if (!_.isNil(newPage)) {
                drvCtrl.currentPage = newPage;
            }

            drvCtrl.loading = true;

            var pageSize = null;
            var entityTemplateId = null;
            if (!angular.isUndefined(drvCtrl.configuration.ListPageSize) && drvCtrl.configuration.ListPageSize !== null) {
                pageSize = angular.copy(drvCtrl.configuration.ListPageSize);
            }
            if (!angular.isUndefined(drvCtrl.configuration.EntityTemplateId) && drvCtrl.configuration.EntityTemplateId !== null) {
                entityTemplateId = angular.copy(drvCtrl.configuration.EntityTemplateId);
            }

            // Search all fields using the same text input
            var searchFields = [];
            if (!_.isNil(drvCtrl.entityTemplate) && !_.isNil(drvCtrl.entityTemplate.SearchFields)) {
                angular.forEach(drvCtrl.entityTemplate.SearchFields, function (searchFieldKey) {
                    searchFields[searchFieldKey] = drvCtrl.searchText;
                });
            }

            updateListPromise.latestOnly(EntitiesService.query({
                entityTemplateId: entityTemplateId,
                pageSize: pageSize,
                currentPage: drvCtrl.currentPage,
                searchFields: searchFields,
                orderFields: drvCtrl.orderFields
            }))
                .then(getEntitiesOk, getEntitiesError)
                .then(function () {
                    drvCtrl.loading = false;
                });

            function getEntitiesOk(data) {
                drvCtrl.entities = data.Entities;
                drvCtrl.entityCount = data.EntitiesCount;
                drvCtrl.error = null;
                drvCtrl.allEntitiesSelected = false;
            }

            function getEntitiesError(response) {
                drvCtrl.error = response;
            }
        }

        function selectedOrderFieldChange() {
            updateOrderFields();
            updateList();
        }

        function updateOrderFields() {
            var currentOrderOption = _.find(drvCtrl.entityTemplate.OrderSetting.Options, function (n) { return n.Name === drvCtrl.selectedOrderOption; });

            if (!angular.isUndefined(currentOrderOption) && currentOrderOption !== null) {
                drvCtrl.orderFields = currentOrderOption.Fields;
            }
        }

        function orderSelectorDisabled() {
            var result = true;

            if (!drvCtrl.error && !angular.isUndefined(drvCtrl.entityTemplate) && drvCtrl.entityTemplate !== null && !angular.isUndefined(drvCtrl.entityTemplate.OrderSetting) && drvCtrl.entityTemplate.OrderSetting.Options.length > 1) {
                result = false;
            }

            return result;
        }

        /**
         * Delete the specified entity
         */
        function deleteEntity(entity) {
            var templateName = getEntityTemplateName();

            DialogService
                .confirm('Delete the selected ' + templateName + '?', 'Delete ' + templateName + ' confirm')
                .then(deleteOk);

            function deleteOk() {
                EntitiesService.remove(entity.Id)
                    .then(deleteEntityOk)
                    .catch(deleteEntityError);

                function deleteEntityOk(success) {
                    drvCtrl.updateList();
                    loadEntityCount();
                }

                function deleteEntityError(response) {
                    DialogService.error('Error deleting Entity', response);
                }
            }
        }

        /**
         * @param {bool} plural - True if fetching the plural name, otherwise the singular name will be fetched.
         * @return {string}
         */
        function getEntityTemplateName(plural) {
            if (plural) {
                if (!_.isNil(drvCtrl.entityTemplate)) {
                    return drvCtrl.entityTemplate.PluralName;
                } else {
                    return 'Entities';
                }
            } else {
                if (!_.isNil(drvCtrl.entityTemplate)) {
                    return drvCtrl.entityTemplate.Name;
                } else {
                    return 'Entity';
                }
            }
        }

        /**
         * Ensure at least one entity is selected.
         * Displays a dialog 'No <entity-template-plural-name> were selected' if no entities are selected.
         * @return {bool} true if at least one entity is selected, else false
         */
        function ensureEntitySelection() {
            if (drvCtrl.selectedEntities.length === 0) {
                DialogService.alert('No ' + getEntityTemplateName(true) + ' were selected.');
                return false;
            } else {
                return true;
            }
        }

        /**
         * Delete the entities selected in the list.
         */
        function deleteEntities() {
            var selectedEntityIds = _.map(drvCtrl.selectedEntities, 'Id');
            var templateName = getEntityTemplateName(selectedEntityIds.length > 1);

            if (ensureEntitySelection()) {
                DialogService
                    .confirm('Delete the ' + selectedEntityIds.length + ' selected ' + templateName + '?', 'Delete ' + templateName + ' confirm')
                    .then(deleteEntitiesConfirmed);
            }

            function deleteEntitiesConfirmed() {
                drvCtrl.loading = true;
                EntitiesService.multiDelete(selectedEntityIds)
                    .then(deleteEntityOk, deleteEntityError);
            }

            function deleteEntityOk(success) {
                drvCtrl.loading = false;
                drvCtrl.updateList();
                loadEntityCount();
            }

            function deleteEntityError(response) {
                drvCtrl.loading = false;
                DialogService.error('Error deleting ' + templateName, response);
            }
        }

        /**
         * Copy the entities selected in the list.
         */
        function copyEntities() {
            var selectedEntityIds = _.map(drvCtrl.selectedEntities, 'Id');
            var templateName = getEntityTemplateName(selectedEntityIds.length > 1);

            if (ensureEntitySelection()) {
                DialogService
                    .confirm('Copy the ' + selectedEntityIds.length + ' selected ' + templateName + '?', 'Copy ' + templateName + ' confirm')
                    .then(copyEntitiesConfirmed);
            }

            function copyEntitiesConfirmed() {
                drvCtrl.loading = true;
                EntitiesService.multiCopy(selectedEntityIds)
                    .then(copyOk, copyError);
            }

            function copyOk() {
                drvCtrl.loading = false;
                drvCtrl.updateList();
                loadEntityCount();
            }

            function copyError(response) {
                drvCtrl.loading = false;
                DialogService.error('Error copying ' + templateName, response);
            }
        }

        /*
         * Export the entities selected in the list.
         */
        function exportEntities() {
            var selectedEntityIds = _.map(drvCtrl.selectedEntities, 'Id');
            var templateName = getEntityTemplateName(selectedEntityIds.length > 1);

             DialogService.show({
                 locals: {
                     entityTemplate: drvCtrl.entityTemplate,
                     entityCount: selectedEntityIds.length,
                     entityPreviewName: null,
                     disableAllEntities: drvCtrl.entities.length < 1
                 },
                    controller: 'ExportEntityDialogCtrl',
                    controllerAs: 'vm',
                    templateUrl: 'app/entity/export-entity-dialog.tmpl.html'
                }).then(exportEntitiesConfirmed);

            function exportEntitiesConfirmed(exportDetails) {
                var entityIds = null;
                var entityTemplateId = null;
                var blankTemplate = null;

                switch (exportDetails.ExportType)
                {
                    case 'allEntities':
                        entityTemplateId = drvCtrl.entityTemplate.Id;
                        break;
                    case 'blankTemplate':
                        entityTemplateId = drvCtrl.entityTemplate.Id;
                        blankTemplate = true;
                        break;
                    case 'currentSelection':
                        entityIds = selectedEntityIds;
                        break;
                }

                EntitiesService.exportEntities(entityIds, entityTemplateId, blankTemplate, exportDetails.IncludeLookups, exportDetails.Transpose, exportDetails.FieldsToExport)
                    .then(exportOk, exportError);
            }

            function exportOk(response) {
                var blob = new Blob([response.data], { type: response.headers('Content-Type') });
                var fileName = response.headers('Content-Disposition').match(/filename[^;\n=]*=((['"]).*?\2|[^;\n]*)/)[1];
                fileName = fileName.replace(/^"/, '');
                fileName = fileName.replace(/"$/, '');
                saveAs(blob, fileName);
            }

            function exportError(response) {
                DialogService.error('Error exporting ' + templateName, response);
            }
        }

        /*
         * Import the entities from the selected file.
         */
        function importEntities() {
            FileSelectorService.selectLocalFileDialog(false, ['xlsx', 'xlsm'])
                .then(function (fileList) {
                    DialogService.show({
                        locals: {
                            entityTemplate: drvCtrl.entityTemplate,
                            file: fileList[0]
                        },
                        controller: 'ImportEntityDialogCtrl',
                        controllerAs: 'vm',
                        templateUrl: 'app/entity/import-entity-dialog.tmpl.html'
                    }).finally(function () {
                        updateList();
                        loadEntityCount();
                    });
                });
        }

        /**
         * Merge document for the selected entities.
         * Prompts the user to choose the document template to use.
         */
        function mergeDocument() {
            if (ensureEntitySelection()) {
                DialogService.show({
                    locals: { entityTemplateId: drvCtrl.configuration.EntityTemplateId, entityId: null, entityTemplateName: drvCtrl.entityTemplate.Name },
                    controller: 'DocumentMergeCtrl',
                    controllerAs: 'vm',
                    templateUrl: 'app/documentmerge/document-merge.tmpl.html'
                }).then(getMergeDetailsOk);
            }

            function getMergeDetailsOk(mergeDetails) {
                angular.forEach(drvCtrl.selectedEntities, function (n) {
                    DocMergeService.merge(n.Id, mergeDetails.DocumentTemplateId, mergeDetails.OutputFormat, mergeDetails.Description,
                        mergeDetails.FileDescription, mergeDetails.FileName, mergeDetails.Tags, mergeDetails.SaveToCurrentEntity,
                        mergeDetails.SaveToCurrentUser, mergeDetails.SaveToOrganisation, mergeDetails.SaveToEntities)
                        .catch(mergeEntityDocumentTemplateError);
                });
            }

            function mergeEntityDocumentTemplateError(response) {
                DialogService.error('Error merging Document', response);
            }
        }

        /**
         * Redirects to the "add note" route
         */
        function addNote() {
            if (ensureEntitySelection()) {
                if (drvCtrl.selectedEntities.length > 1) {
                    return DialogService.alert('Add Note can only be used on one ' + getEntityTemplateName() + ' at a time');
                }

                $location.url('notes/new?relatedItemId=' + drvCtrl.selectedEntities[0].Id);
            }
        }

        /**
         * Redirects to the "add file" route
         */
        function addFile() {
            if (ensureEntitySelection()) {
                if (drvCtrl.selectedEntities.length > 1) {
                    return DialogService.alert('Add File can only be used on one ' + getEntityTemplateName() + ' at a time');
                }

                $location.url('files/new?relatedItemId=' + drvCtrl.selectedEntities[0].Id);
            }
        }

        /**
         * @returns {string} URL for "add entity" link.
         */
        function newEntityLink() {
            var result = 'entities/new';

            if (!angular.isUndefined(drvCtrl.configuration) && !angular.isUndefined(drvCtrl.configuration.EntityTemplateId) && drvCtrl.configuration.EntityTemplateId !== null && drvCtrl.configuration.EntityTemplateId !== '0') {
                result = result + '?entityTemplateId=' + drvCtrl.configuration.EntityTemplateId;
            }

            return result;
        }

        /**
         * Redirects to the compose email route
         */
        function composeEmails() {
            if (ensureEntitySelection()) {
                var path = '';

                var selectedEntityIds = _.map(drvCtrl.selectedEntities, 'Id');

                angular.forEach(selectedEntityIds, function (entityId) {
                    path += path.length === 0 ? '?' : '&';
                    path += 'fromEntityIds=' + entityId;
                });

                $location.url('email/new' + path);
            }
        }

        /**
         * Redirects to the compose SMS route
         */
        function composeSMS() {
            if (ensureEntitySelection()) {
                var path = '';

                var selectedEntityIds = _.map(drvCtrl.selectedEntities, 'Id');

                angular.forEach(selectedEntityIds, function (entityId) {
                    path += path.length === 0 ? '?' : '&';
                    path += 'fromEntityIds=' + entityId;
                });

                $location.url('sms' + path);
            }
        }

        /**
         * Redirects to the add task route
         */
        function addTask() {
            if (ensureEntitySelection()) {
                if (drvCtrl.selectedEntities.length > 1) {
                    return DialogService.alert('Add Task can only be used on one ' + getEntityTemplateName() + ' at a time');
                }

                $location.url('tasks/new?entityId=' + drvCtrl.selectedEntities[0].Id);
            }
        }

        /**
         * Runs workflow for the currently selected entity
         */
        function runWorkflow() {
            if (ensureEntitySelection()) {
                var selectedEntityIds = _.map(drvCtrl.selectedEntities, 'Id');

                if (selectedEntityIds.length === 0) {
                    DialogService.alert('No ' + getEntityTemplateName(true) + ' were selected');
                } else if (selectedEntityIds.length > 1) {
                    DialogService.alert('Workflow can only be run on one ' + getEntityTemplateName());
                } else {
                    WorkflowRunService.promptWorkflowStart(selectedEntityIds[0]);
                }
            }
        }

        /**
         * Redirects to the new entity route
         */
        function newEntity() {
            $location.url(drvCtrl.newEntityLink());
        }

        /**
         * When the view is 'closed' navigation to a previous page should occur.
         */
        function close() {
            NavigationAidService.goBack();
        }

        /**
         * Go to the full screen mode for the entity template
        */
        function fullscreen() {
            $location.url('entities?entityTemplateId=' + drvCtrl.configuration.EntityTemplateId);
        }

        /**
         * Get the title text, based on entity template and show deleted toggle
         *
         * @returns {string}
         */
        function getTitleText() {
            var result = drvCtrl.label;

            if (drvCtrl.showDeleted && !_.isNil(result) && result !== '') {
                result = 'Deleted ' + result;
            }

            return result;
        }

        /**
         * Operations to performn when show deleted value is changed
         */
        function showDeletedChanged() {
            drvCtrl.updateList();
            loadEntityCount();
        }
    }
})();